import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Code, Save, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const AdminScripts = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scripts, setScripts] = useState({
    header_scripts: '',
    body_scripts: '',
    footer_scripts: ''
  });

  useEffect(() => {
    fetchScripts();
  }, []);

  const fetchScripts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/scripts`);
      setScripts({
        header_scripts: response.data.header_scripts || '',
        body_scripts: response.data.body_scripts || '',
        footer_scripts: response.data.footer_scripts || ''
      });
    } catch (error) {
      console.error('Error fetching scripts:', error);
      toast.error('Kunne ikke laste scripts');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(
        `${API}/scripts`,
        scripts,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Scripts lagret!');
    } catch (error) {
      console.error('Error saving scripts:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke lagre scripts');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setScripts(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white">Laster scripts...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Global Scripts Manager
            </span>
          </h2>
          <p className="text-gray-400">
            Injiser custom scripts (Plausible, Clarity, Ahrefs, etc.) i nettstedet ditt
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Lagrer...' : 'Lagre Scripts'}
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-br from-blue-900/30 to-blue-800/30 border-blue-700/50">
        <CardContent className="p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-300">
              <p className="font-semibold text-white mb-2">Viktig informasjon om scripts:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Header Scripts:</strong> Limes inn i {'<head>'} - Bruk for tracking scripts, meta tags, CSS</li>
                <li><strong>Body Scripts (Top):</strong> Limes inn rett etter {'<body>'} - Bruk for GTM, noscript tags</li>
                <li><strong>Footer Scripts:</strong> Limes inn før {'</body>'} - Bruk for analytics, chat widgets</li>
              </ul>
              <p className="mt-3 text-yellow-400">⚠️ Vær forsiktig: Ugyldig kode kan ødelegge nettstedet!</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Header Scripts */}
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <Code className="w-5 h-5 text-purple-400" />
            <span>Header Scripts</span>
            <Badge variant="outline" className="ml-2 text-xs">{'<head>'}</Badge>
          </CardTitle>
          <CardDescription className="text-gray-400">
            Scripts som skal limes inn i {'<head>'} seksjonen. Eksempel: Google Analytics, Plausible, metadata
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={scripts.header_scripts}
            onChange={(e) => handleChange('header_scripts', e.target.value)}
            placeholder={`<!-- Eksempel: Google Analytics -->
<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'G-XXXXXXXXXX');
</script>`}
            className="min-h-[200px] font-mono text-sm bg-gray-900 border-gray-700 text-gray-300"
          />
        </CardContent>
      </Card>

      {/* Body Scripts */}
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <Code className="w-5 h-5 text-blue-400" />
            <span>Body Scripts (Top)</span>
            <Badge variant="outline" className="ml-2 text-xs">After {'<body>'}</Badge>
          </CardTitle>
          <CardDescription className="text-gray-400">
            Scripts som skal limes inn tidlig i {'<body>'}. Eksempel: Google Tag Manager, noscript tags
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={scripts.body_scripts}
            onChange={(e) => handleChange('body_scripts', e.target.value)}
            placeholder={`<!-- Eksempel: Google Tag Manager -->
<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-XXXXXXX"
height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>`}
            className="min-h-[200px] font-mono text-sm bg-gray-900 border-gray-700 text-gray-300"
          />
        </CardContent>
      </Card>

      {/* Footer Scripts */}
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <Code className="w-5 h-5 text-green-400" />
            <span>Footer Scripts</span>
            <Badge variant="outline" className="ml-2 text-xs">Before {'</body>'}</Badge>
          </CardTitle>
          <CardDescription className="text-gray-400">
            Scripts som skal limes inn før {'</body>'}. Eksempel: Chat widgets, analytics, defer scripts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={scripts.footer_scripts}
            onChange={(e) => handleChange('footer_scripts', e.target.value)}
            placeholder={`<!-- Eksempel: Microsoft Clarity -->
<script type="text/javascript">
  (function(c,l,a,r,i,t,y){
    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
  })(window, document, "clarity", "script", "XXXXXXXXX");
</script>`}
            className="min-h-[200px] font-mono text-sm bg-gray-900 border-gray-700 text-gray-300"
          />
        </CardContent>
      </Card>

      {/* Save Button (Bottom) */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          size="lg"
          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'Lagrer...' : 'Lagre Alle Scripts'}
        </Button>
      </div>
    </div>
  );
};

export default AdminScripts;
